// SplitRow — one PO-line split inside a ChargeCard in RecordInvoiceModal.
// Extracted so the card and the modal stay holdable. Presentational: it renders
// the draft split and reports edits via onPatch. A line <select> (each option
// shows the line's number + description and its live pre-this-invoice
// remaining, "Line N — description · $X left", plus a "(≈ N unit[s])"
// quantity-remaining hint when the parent charge is a rated product), an
// optional row-quantity input (shown only for a multi-split rated charge —
// ChargeCard decides via showQty/qtyDisplay), and a money amount input. Under
// the row it renders the live axis-1 feedback — the before → after balance for
// the chosen line drawn down by this split, with the same quantity-remaining
// hint appended — styled red when the draw would overdraw the line and amber
// when it lands near the limit. When another in-review invoice draws from the
// same line an amber contention icon carries the same tooltip vocabulary as
// the review-queue row detail (escalating when approving both would overdraw).
// When the modal's qty<->amount sync (applySplitSync) auto-adjusted this row's
// amount or qty, `highlightField` rings that field's wrapper and prints a
// caption beneath the feedback strip so the silent money change is visible;
// the modal clears it on a timer or the next edit.

import { AlertTriangle, Trash2 } from "lucide-react";
import CurrencyInput from "../../common/CurrencyInput";
import NumberInput from "../../common/NumberInput";
import { validateLineBalance, formatAud } from "../../../utils/financeCalc";
import { parseMoney, type SplitDraft, type LineContention } from "./invoiceFormState";
import { formatQtyLeft } from "./qtyLeft";
import type { PoLine, LineBalance } from "../../../types/finance";

interface SplitRowProps {
  split: SplitDraft;
  poLines: PoLine[];
  lineBalances: Record<string, LineBalance>;
  contention?: LineContention;
  error?: string;
  /** Agreed rate + unit of the parent charge, when it is a rated product —
   *  drives the quantity-remaining hint beside every dollar balance. */
  chargeRate?: number | null;
  chargeUnit?: string;
  /** Render the row-quantity input (multi-split rated charges only). */
  showQty?: boolean;
  /** Resolved display value: the entered qty, or the derived amount ÷ rate
   *  seed when untouched. */
  qtyDisplay?: string;
  /** Set when the modal's qty<->amount sync just auto-adjusted this field —
   *  rings its wrapper and prints a caption. */
  highlightField?: "amount" | "qty" | null;
  onPatch: (patch: Partial<SplitDraft>) => void;
  onRemove: () => void;
  canRemove: boolean;
  disabled?: boolean;
}

function lineOptionLabel(
  line: PoLine,
  remaining: number | undefined,
  chargeRate?: number | null,
  chargeUnit?: string,
): string {
  const base = `Line ${line.lineNumber} — ${line.description}`;
  if (remaining === undefined) return base;
  const qty = formatQtyLeft(remaining, chargeRate, chargeUnit);
  return `${base} · ${formatAud(remaining)} left${qty ? ` (${qty})` : ""}`;
}

export default function SplitRow({
  split,
  poLines,
  lineBalances,
  contention,
  error,
  chargeRate,
  chargeUnit,
  showQty,
  qtyDisplay,
  highlightField,
  onPatch,
  onRemove,
  canRemove,
  disabled,
}: SplitRowProps) {
  const lineId = `${split.key}-line`;
  const amountId = `${split.key}-amount`;

  const lb = split.poLineId ? lineBalances[split.poLineId] : undefined;
  const amount = parseMoney(split.amount);
  const balance =
    lb && amount !== null
      ? validateLineBalance(
          { remaining: lb.remaining, lineTotal: lb.lineTotal },
          amount,
        )
      : null;
  const after = lb ? lb.remaining - (amount ?? 0) : 0;
  const qtyAfter = lb ? formatQtyLeft(after, chargeRate, chargeUnit) : null;

  const balanceClass =
    balance?.kind === "over"
      ? "text-red-600 dark:text-red-400"
      : balance?.kind === "warn"
        ? "text-amber-700 dark:text-amber-400"
        : "text-slate-500 dark:text-slate-400";

  const pending = contention?.pendingElsewhere ?? 0;
  const jointOverdraw = lb ? after - pending < -0.005 : false;
  const contentionTitle =
    pending > 0
      ? `Invoice ${contention?.contendingInvoiceNumbers.join(", ")} (in review) ` +
        `also draws ${formatAud(pending)} from this line` +
        (jointOverdraw
          ? ` — if both are approved this line overdraws by ${formatAud(-(after - pending))}`
          : "")
      : "";

  return (
    <div data-testid="invoice-split-row" className="space-y-1">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <label htmlFor={lineId} className="sr-only">
            PO line
          </label>
          <select
            id={lineId}
            aria-label="PO line"
            className="input-sm w-full"
            value={split.poLineId}
            onChange={(e) => onPatch({ poLineId: e.target.value })}
            disabled={disabled}
            aria-invalid={!!error}
          >
            <option value="">Choose a PO line…</option>
            {poLines.map((line) => (
              <option key={line.id} value={line.id}>
                {lineOptionLabel(line, lineBalances[line.id]?.remaining, chargeRate, chargeUnit)}
              </option>
            ))}
          </select>
        </div>
        {showQty && (
          <div
            className={`w-20 shrink-0 ${
              highlightField === "qty" ? "ring-2 ring-emerald-400 rounded-md" : ""
            }`}
          >
            <label htmlFor={`${split.key}-qty`} className="sr-only">
              Row quantity
            </label>
            <NumberInput
              id={`${split.key}-qty`}
              size="sm"
              ariaLabel="Row quantity"
              className="w-full"
              value={qtyDisplay ?? ""}
              onChange={(raw) => onPatch({ qty: raw })}
              disabled={disabled}
            />
          </div>
        )}
        <div
          className={`w-32 shrink-0 ${
            highlightField === "amount" ? "ring-2 ring-emerald-400 rounded-md" : ""
          }`}
        >
          <label htmlFor={amountId} className="sr-only">
            Split amount
          </label>
          <CurrencyInput
            id={amountId}
            size="sm"
            ariaLabel="Split amount"
            className="w-full"
            value={split.amount}
            onChange={(raw) => onPatch({ amount: raw })}
            ariaInvalid={!!error}
            disabled={disabled}
          />
        </div>
        <div className="flex h-8 shrink-0 items-center">
          {canRemove && (
            <button
              type="button"
              aria-label="Remove split"
              onClick={onRemove}
              disabled={disabled}
              className="btn-icon-ghost btn-icon-sm text-slate-500 hover:text-red-600 dark:text-slate-400 dark:hover:text-red-400"
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 pl-0.5">
        {balance && (
          <span
            data-testid={`split-after-${split.key}`}
            title={`${formatAud(lb!.remaining)} before → ${formatAud(after)} after`}
            className={`text-caption font-medium tabular-nums ${balanceClass}`}
          >
            Line balance after: {formatAud(after)}
            {qtyAfter ? ` · ${qtyAfter} left` : ""}
          </span>
        )}
        {pending > 0 && (
          <span
            data-testid={`split-contention-${split.key}`}
            title={contentionTitle}
            className="inline-flex items-center gap-1 text-caption text-amber-700 dark:text-amber-400"
          >
            <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden="true" />
            <span className="sr-only">{contentionTitle}</span>
            <span aria-hidden="true">Also in review</span>
          </span>
        )}
        {error && (
          <span className="text-caption text-red-600 dark:text-red-400">{error}</span>
        )}
      </div>
      {highlightField && (
        <span
          aria-live="polite"
          className="text-caption font-medium text-emerald-700 dark:text-emerald-400"
        >
          {highlightField === "amount"
            ? "Amount adjusted to match the quantity"
            : "Quantity re-derived from the amount"}
        </span>
      )}
    </div>
  );
}
